/**
 * The teacher registry (И7 — «Реестр педагогов», tasks T.F6 + T.R7): the roster and the
 * right-side row drawer.
 *
 * ROUTER: `/teachers` (a path route, not a query param like the venue card) — the
 * roster is a top-level destination with its own nav item, and the venue card deep
 * links into it with `/teachers?venueId=12`, which this page reads as its filter, so
 * the round trip venue card → «Педагоги» → back keeps the card's context.
 *
 * T.R7 — the row drawer rides on the LIST URL (`?teacherId=N`), exactly the way the
 * applications profile rides on `?pguId=`: a click on a row opens the panel, closing it
 * restores the roster's own filters, and the drawer survives a reload. There are no
 * inline «Изменить»/«Удалить» buttons any more — the row itself is the affordance, and
 * every write (the 19 attribute fields included) lives inside the panel.
 *
 * WRITE GATING — the api is the authority; this page only hides what the actor may not
 * do (`canWriteTeachers`, teachers-model: a МЕНЕДЖЕР/АДМИН as always, and since v1.9.11
 * (T.Z71) a КУРАТОР-role session with a non-empty cabinet scope — the exact shape #7.5's
 * POST/PATCH admit, the venue scope enforced server-side). A link-less VIEWER sees the
 * registry, the search, the venue filter and a read-only drawer; the form and the
 * «Удалить» in its footer are rendered only for the roles that could actually use them.
 * ADDITIVE (v1.9.12, T.Z80) — the delete follows the same predicate
 * (`canDeleteTeachers`): #7.5's DELETE opened to the same `'scopedCurator'` grant, so a
 * МЕНЕДЖЕР and a scoped КУРАТОР see the footer's «Удалить» too.
 *
 * T.Z69 — a FAILED #6 directory read renders as an error state with «Повторить», never
 * as «Ничего не найдено»: the api's refusal used to fold into `data ?? []` and the
 * «Площадка» combobox told the curator «no such venue» over what was really 403.
 *
 * T.Z77 — «Добавить педагога» prefills the create form's «Площадка» from the page's
 * own venue filter, so «фильтр → добавить» does not re-ask the question the page has
 * already answered.
 *
 * T.Z80 — the delete sits behind its own CONFIRMATION drawer, the cabinet's #26.4
 * shape (`PrefillDeleteDrawer`): «Удалить» REPLACES the editor drawer with a drawer
 * that names the педагог and the consequences, and only its «Удалить» reaches the api
 * (`deleteRequestId` is the one producer of the call). Never a nested overlay, never a
 * one-click delete.
 *
 * ADDITIVE (v1.9.17) — three things land on this page at once:
 *   * T.Z90 — the funnel CHIPS («Всего / Подтверждено / Ожидают подтверждения /
 *     Добавлены кураторами»), the cabinet's summary-chips shape, single-select, the
 *     counts from `meta.counts`, filtering the table server-side through `?status=` /
 *     `?createdVia=` (the «ожидают» chip is the union UNCONFIRMED+FOR_DELETION — the
 *     two states awaiting a person's decision). T.Z102 (owner, 16.09) moved their ROW
 *     up: after the «Педагоги» heading, BEFORE the table card — T.Z90 had first put
 *     it under the filter bar inside the card, and the owner read the funnel as the
 *     page's opening summary, not as a filter-bar attachment;
 *   * T.Z89 — the status is a first-class fact: a «Статус» column with the badge, the
 *     drawer's headline badge, an editor for the roles the api allows (ADMIN/
 *     МЕНЕДЖЕР), and the delete's confirmation copy that says WHICH action the click
 *     is (an ADMIN removes; a МЕНЕДЖЕР/КУРАТОР marks «на удаление»);
 *   * T.Z91 — a КУРАТОР session renders the REDUCED drawer: the api omits the ten
 *     cells and the page skips their three sections («КПК», «Договор», «Документы и
 *     согласия»), and its patch body can never carry them.
 */

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../api/client';
import { invalidateTeachers, TEACHERS_PAGE_SIZE, useCreateTeacher, useDeleteTeacher, usePatchTeacher, useTeachers } from '../../api/teachers';
import type { TeacherDto, TeacherStatus } from '../../api/teachers';
import { ExportButton } from '../../components/ExportButton';
import { useVenueDirectory } from '../../api/venues';
import { useAuth } from '../../auth/AuthContext';
import { Combobox } from '../../components/Combobox';
import { Button, Drawer, EmptyState, ErrorState, Field, Input, Modal, Pagination, Textarea } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams } from '../../lib/query-params';

import './teachers.css';
import {
  attributeFieldsFor,
  attributeLabel,
  attributeSectionsFor,
  canWriteTeachers,
  chipCountOf,
  chipQueryOf,
  EDITOR_DELETE_LAYER,
  EMPTY_DRAFT,
  PARAM,
  statusAccent,
  statusLabel,
  statusOptionsFor,
  TEACHER_CHIPS,
  TEACHER_VENUE_DIRECTORY_ERROR,
  createVenuePrefill,
  deleteRequestId,
  draftHasErrors,
  draftOf,
  emailLabel,
  parseTeacherParams,
  phoneLabel,
  teacherCountLabel,
  teacherDeleteCopy,
  teacherFiltersOn,
  toCreateBody,
  toDeleteConfirm,
  toPatchBody,
  toPositiveInt,
  totalPages,
  validateDraft,
  venueComboboxOptions,
  venueDirectoryState,
  venueLabelOf,
  venueOptionsInScope,
  type DraftErrors,
  type TeacherDeleteLayer,
  type TeacherDraft,
  type VenueDirectoryState,
} from './teachers-model';

export function TeachersPage() {
  const { params, setParams } = useQueryParams();
  const list = useMemo(() => parseTeacherParams(params), [params]);

  return <TeachersRoster params={list} setParams={setParams} />;
}

// ---------------------------------------------------------------------------
// The roster
// ---------------------------------------------------------------------------

function TeachersRoster({
  params,
  setParams,
}: {
  params: ReturnType<typeof parseTeacherParams>;
  setParams: ReturnType<typeof useQueryParams>['setParams'];
}) {
  // ADDITIVE (v1.9.11, T.Z71) — the session's cabinet scope rides along: it decides
  // whether a КУРАТОР-role session sees the write controls at all (the api enforces the
  // venue scope itself; this only keeps the controls honest), and it locks the forms'
  // venue pickers to the same venue set.
  const { role, cabinetVenueIds } = useAuth();
  const canEdit = canWriteTeachers(role, cabinetVenueIds);

  const directory = useVenueDirectory();
  // T.Z69 — the state of the dictionary read drives the «Площадка» field: a FAILURE is
  // an error state with «Повторить» (below), never an empty combobox.
  const venueState = venueDirectoryState(directory);
  const options = useMemo(
    () => venueOptionsInScope(venueComboboxOptions(directory.data ?? []), role, cabinetVenueIds),
    [directory.data, role, cabinetVenueIds],
  );

  // T.Z72 — «Отдел» has left this page; the roster narrows by venue and search only.
  // ADDITIVE (v1.9.17, T.Z90) — the active chip asks its api filter (`?status=` list
  // or `?createdVia=`), and the counts come back in `meta.counts` regardless.
  const chipFilter = params.chip !== undefined ? chipQueryOf(params.chip) : {};
  const roster = useTeachers({ venueId: params.venueId, q: params.q, ...chipFilter, page: params.page });
  const teachers: TeacherDto[] = roster.data?.teachers ?? [];
  const meta = roster.data?.meta;

  // «Поиск» is committed on submit, not per keystroke — each commit is a server
  // round-trip and a pager click must not drag a half-typed needle along (venues rule).
  const [qDraft, setQDraft] = useState(params.q ?? '');
  const filtersOn = teacherFiltersOn(params);

  /** T.Z69 — the one retry the «Площадка» error states offer (the filter and the forms). */
  const retryVenue = () => void directory.refetch();

  const applyFilters = (next: { venueId?: number; q?: string }) => {
    setQDraft(next.q ?? '');
    setParams({
      [PARAM.VENUE_ID]: next.venueId,
      [PARAM.Q]: next.q,
      [PARAM.PAGE]: undefined, // a new filter is a new result set
    });
  };

  const [creator, setCreator] = useState(false);
  const closeDrawer = () => {
    setDeleteLayer(EDITOR_DELETE_LAYER);
    setParams({ [PARAM.TEACHER_ID]: undefined });
  };
  // ADDITIVE (v1.9.12, T.Z80) — the delete's second layer, the cabinet's #26.4 shape:
  // while a delete is being confirmed the editor drawer is UNMOUNTED (the overlays are
  // never nested) and the confirmation drawer stands alone; «Отмена» returns to the
  // editor, a confirmed delete closes both. The call itself is produced only inside
  // `TeacherDeleteConfirm`, through `deleteRequestId`.
  const [deleteLayer, setDeleteLayer] = useState<TeacherDeleteLayer>(EDITOR_DELETE_LAYER);
  // The drawer is URL-driven, so the row has to come out of the loaded page. A stale
  // `?teacherId=` (the roster moved under it) is an empty panel with a way out, never
  // a crash and never a write to a row that is not on screen.
  const selected = teachers.find((teacher) => teacher.id === params.teacherId) ?? null;

  return (
    <>
      <div className="page-header">
        <h1>Педагоги</h1>
        <p className="page-header__sub">
          Реестр педагогов по площадкам: ФИО, контакты и привязка к площадке.
        </p>
      </div>

      {/* ADDITIVE (v1.9.17, T.Z90) — the funnel chips, the cabinet's summary-chips
          shape (a button per bucket, `aria-pressed`, the count inside): «всего»
          clears, the other three filter the table server-side. The numbers are
          `meta.counts` — the api's funnel over the scope the page reads, so a
          chip's own figure never moves because that chip is the active filter.
          Single-select on purpose: «добавлены кураторами» is the PROVENANCE axis,
          which overlaps the status axis instead of partitioning it.
          T.Z102 (owner, 16.09) — the row lives directly under the «Педагоги»
          heading, OUTSIDE the table card: the funnel is the page's opening
          summary (the cabinet's SummaryBlock / the dashboard's .kpi-row figure —
          self-carded tiles on the page background), not an attachment of the
          filter bar. The card below keeps the filters and the table. */}
      <div className="teachers-chips" role="group" aria-label="Воронка статусов" data-testid="teachers-chips">
        <button
          type="button"
          className={[
            'teachers-chip',
            'teachers-chip--neutral',
            'teachers-chip--button',
            params.chip === undefined ? 'teachers-chip--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-testid="teachers-chip-all"
          aria-pressed={params.chip === undefined}
          onClick={() => setParams({ [PARAM.CHIP]: undefined, [PARAM.PAGE]: undefined })}
        >
          <span className="teachers-chip__num">{meta?.counts ? meta.counts.total : (meta?.total ?? teachers.length)}</span>
          <span className="teachers-chip__label">Всего</span>
        </button>
        {TEACHER_CHIPS.map((chip) => {
          const active = params.chip === chip.value;
          const count = chipCountOf(chip.value, meta?.counts);
          return (
            <button
              key={chip.value}
              type="button"
              className={[
                'teachers-chip',
                `teachers-chip--${chip.value}`,
                'teachers-chip--button',
                active ? 'teachers-chip--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-testid={`teachers-chip-${chip.value}`}
              aria-pressed={active}
              title={active ? `Снять фильтр «${chip.label}»` : chip.title}
              onClick={() => setParams({ [PARAM.CHIP]: active ? undefined : chip.value, [PARAM.PAGE]: undefined })}
            >
              <span className="teachers-chip__num">{count === null ? '—' : count}</span>
              <span className="teachers-chip__label">{chip.label}</span>
            </button>
          );
        })}
      </div>

      <div className="card card--flush teacher-section">
        <form
          className="toolbar teachers-toolbar teachers-toolbar--filters"
          data-testid="teachers-filter"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            applyFilters({ venueId: params.venueId, q: qDraft.trim() });
          }}
        >
          {/* Wave 6 (owner) — «Площадка» takes its own full-width row and «Поиск»
              leads the row under it: a venue name plus an address is the longest
              label on the page, and the roster's first question is «which venue?».
              Same shape the quotas and applications bars use.
              T.Z69 — a FAILED #6 read renders the error with a retry instead of the
              combobox: «Ничего не найдено» over zero options told the user «нет такой
              площадки» when the truth was «словарь не пришёл» (the api's 403 on the
              undecorated #6 before v1.9.11). */}
          <div className="toolbar__field toolbar__field--venue">
            <label className="toolbar__label" htmlFor="teachers-venue">
              Площадка
            </label>
            {venueState.kind === 'error' ? (
              <div className="teachers-venue-error" role="alert" data-testid="teachers-venue-error">
                <span className="teachers-venue-error__text">{TEACHER_VENUE_DIRECTORY_ERROR}</span>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  data-testid="teachers-venue-retry"
                  onClick={() => void directory.refetch()}
                >
                  Повторить
                </button>
              </div>
            ) : (
              <Combobox
                id="teachers-venue"
                testId="teachers-venue"
                value={params.venueId === undefined ? '' : String(params.venueId)}
                options={options}
                placeholder="все площадки"
                clearLabel="Сбросить площадку"
                ariaLabel="Фильтр по площадке"
                onChange={(value) => applyFilters({ venueId: toPositiveInt(value) || undefined, q: qDraft.trim() })}
              />
            )}
          </div>
          <div className="toolbar__row">
          {/* T.Z72 (owner) — «Поиск» takes the room the row has: the field grows into
              the space the removed «Отдел» select and the spacer used to sit in, so a
              long needle stays visible instead of riding the input's intrinsic width. */}
          <div className="toolbar__field toolbar__field--search">
            <label className="toolbar__label" htmlFor="teachers-q">
              Поиск
            </label>
            <Input
              id="teachers-q"
              data-testid="teachers-q"
              value={qDraft}
              placeholder="ФИО, телефон или почта"
              onChange={(event) => setQDraft(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary btn--sm" data-testid="teachers-apply">
            Найти
          </button>
          {filtersOn ? (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              data-testid="teachers-reset"
              onClick={() => applyFilters({})}
            >
              Сбросить
            </button>
          ) : null}
          <p className="toolbar__meta" data-testid="teachers-total">
            {teacherCountLabel(meta?.total ?? teachers.length)}
          </p>
          {canEdit ? (
            <Button variant="primary" size="sm" data-testid="teachers-add" onClick={() => setCreator(true)}>
              Добавить педагога
            </Button>
          ) : null}
          {/* T.E1 — the registry as a file, scoped by the venue and the search needle.
              MANAGER+ (the component gates itself off a VIEWER, like the api does).
              ADDITIVE (v1.8.9, T.Z56) — «Отдел» does NOT travel: #18.2 has no such key
              (an unknown export key answers 400), and a file that quietly ignored the
              visible filter would be worse than a wider file. */}
          <ExportButton resource="teachers" testId="teachers-export" params={{ venueId: params.venueId, q: params.q }} />
          </div>
        </form>

        {roster.isPending ? (
          <div className="teacher-section__body">
            <RosterSkeleton />
          </div>
        ) : roster.isError ? (
          <div className="teacher-section__body">
            <ErrorState
              message={describeFailure(roster.error as ApiError | null, 'Не удалось загрузить педагогов.')}
              onRetry={() => void roster.refetch()}
            />
          </div>
        ) : teachers.length === 0 ? (
          <EmptyState
            title="Педагогов не найдено"
            hint={
              filtersOn
                ? 'Фильтр ничего не отобрал — ослабьте условие.'
                : 'Реестр заполняется при импорте площадок или вручную.'
            }
            action={
              filtersOn ? (
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => applyFilters({})}>
                  Сбросить фильтр
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap>
              <table className="data" data-testid="teachers-table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th>Телефон</th>
                    <th>Почта</th>
                    <th>Площадка</th>
                    {/* ADDITIVE (v1.9.17, T.Z89) — the status rides the table: the ADMIN
                        reviews the «на удаление» queue and the chips' filter is visible
                        in the rows it filtered. */}
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher) => (
                    <tr
                      key={teacher.id}
                      data-testid="teacher-row"
                      data-teacher-id={teacher.id}
                      data-status={teacher.status}
                      className="teacher-row"
                      aria-selected={teacher.id === params.teacherId}
                      onClick={() => setParams({ [PARAM.TEACHER_ID]: teacher.id })}
                    >
                      <td className="cell-primary" data-testid="row-full-name">
                        {teacher.fullName}
                      </td>
                      <td className={teacher.phone ? undefined : 'cell-muted'} data-testid="row-phone">
                        {phoneLabel(teacher.phone)}
                      </td>
                      <td className={teacher.email ? undefined : 'cell-muted'} data-testid="row-email">
                        {emailLabel(teacher.email)}
                      </td>
                      <td data-testid="row-venue">{venueLabelOf(teacher, options)}</td>
                      <td data-testid="row-status">
                        <span className={`teachers-status teachers-status--${statusAccent(teacher.status)}`}>
                          {statusLabel(teacher.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
            <Pagination
              meta={{
                page: meta?.page ?? params.page,
                totalPages: totalPages(meta?.total ?? 0, meta?.pageSize ?? TEACHERS_PAGE_SIZE),
                total: meta?.total ?? 0,
                limit: meta?.pageSize ?? TEACHERS_PAGE_SIZE,
              }}
              onPage={(page) => setParams({ [PARAM.PAGE]: page > 1 ? page : undefined })}
            />
          </>
        )}
      </div>

      {params.teacherId !== undefined && deleteLayer.kind === 'editor' ? (
        <Drawer open onClose={closeDrawer} title="Педагог" description="Реестровая строка и её поля.">
          {selected === null ? (
            <EmptyState
              title="Педагог не на этой странице"
              hint="Строка с таким id не входит в текущую выборку — сбросьте фильтр и откройте её заново."
              action={
                <button type="button" className="btn btn--secondary btn--sm" onClick={closeDrawer}>
                  Закрыть
                </button>
              }
            />
          ) : (
            <TeacherDrawer
              teacher={selected}
              options={options}
              venueState={venueState}
              onRetryVenue={retryVenue}
              onClose={closeDrawer}
              onDelete={(teacher) => setDeleteLayer(toDeleteConfirm(teacher))}
            />
          )}
        </Drawer>
      ) : null}

      {/* ADDITIVE (v1.9.12, T.Z80) — the confirmation layer. It replaces (never nests
          over) the editor drawer, names the педагог and the consequences, and is the
          only place the DELETE request can be produced from. A delete that worked
          closes BOTH layers — the confirmation and the `?teacherId=` drawer — exactly
          the T.Z24 lesson the cabinet's delete already learned. */}
      {deleteLayer.kind === 'confirm' ? (
        <TeacherDeleteConfirm
          layer={deleteLayer}
          onClose={() => setDeleteLayer(EDITOR_DELETE_LAYER)}
          onDeleted={() => {
            setDeleteLayer(EDITOR_DELETE_LAYER);
            closeDrawer();
          }}
        />
      ) : null}

      {canEdit && creator ? (
        <TeacherCreator
          // T.Z77 — the page's «Площадка» filter seeds the form's venue field, so
          // «фильтр → Добавить педагога» does not re-ask the venue.
          initialVenueId={createVenuePrefill(params.venueId, options)}
          options={options}
          venueState={venueState}
          onRetryVenue={retryVenue}
          onClose={() => setCreator(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// The row drawer (T.R7) — identity, the grouped attribute form, the confirmed delete
// ---------------------------------------------------------------------------

function TeacherDrawer({
  teacher,
  options,
  venueState,
  onRetryVenue,
  onClose,
  onDelete,
}: {
  teacher: TeacherDto;
  options: ReturnType<typeof venueComboboxOptions>;
  venueState: VenueDirectoryState;
  onRetryVenue: () => void;
  onClose: () => void;
  /** ADDITIVE (v1.9.12, T.Z80) — opens the confirmation drawer over this row. */
  onDelete: (teacher: TeacherDto) => void;
}) {
  const { role, cabinetVenueIds } = useAuth();
  // ADDITIVE (v1.9.11, T.Z71) — the same gate the toolbar uses: a scoped КУРАТОР gets
  // the editing drawer, a link-less VIEWER stays read-only. ADDITIVE (v1.9.12, T.Z80)
  // — the delete rides the SAME predicate (`canDeleteTeachers` is the api's DELETE
  // grant, which since v1.9.12 is the same `'scopedCurator'` shape as the writes), so
  // the editor renders the footer's «Удалить» whenever it renders at all.
  const canEdit = canWriteTeachers(role, cabinetVenueIds);

  return (
    <div className="teacher-drawer" data-testid="teacher-drawer" data-teacher-id={teacher.id}>
      <header className="teacher-drawer__head">
        <h3 className="teacher-drawer__name" data-testid="drawer-full-name">
          {teacher.fullName}
        </h3>
        <p className="teacher-drawer__venue" data-testid="drawer-venue">
          {venueLabelOf(teacher, options)}
        </p>
        {/* ADDITIVE (v1.9.17, T.Z89) — the status is the drawer's headline fact: it is
            what the «удалить» click changes and what the ADMIN's review queue reads. */}
        <p className="teacher-drawer__status" data-testid="drawer-status">
          <span className={`teachers-status teachers-status--${statusAccent(teacher.status)}`}>
            {statusLabel(teacher.status)}
          </span>
        </p>
      </header>

      <section className="teacher-drawer__section" aria-label="Контакты">
        <h4 className="teacher-drawer__title">Контакты и привязка</h4>
        {canEdit ? (
          <TeacherEditor teacher={teacher} options={options} venueState={venueState} onRetryVenue={onRetryVenue} onClose={onClose} onDelete={onDelete} />
        ) : (
          <TeacherReadonly teacher={teacher} options={options} />
        )}
      </section>
    </div>
  );
}

/**
 * The read-only half: every field the api answers, in the drawer's own order.
 * ADDITIVE (v1.9.17, T.Z91) — a КУРАТОР session sees the REDUCED set: the api omits
 * the ten cells and this render path skips their three sections, so the drawer never
 * shows an em dash where the truth is "not for your role".
 */
function TeacherReadonly({
  teacher,
  options,
}: {
  teacher: TeacherDto;
  options: ReturnType<typeof venueComboboxOptions>;
}) {
  const { role } = useAuth();
  const sections = attributeSectionsFor(role);
  return (
    <>
      <dl className="teacher-drawer__grid" data-testid="drawer-contacts">
        <div>
          <dt>Телефон</dt>
          <dd data-testid="drawer-phone">{phoneLabel(teacher.phone)}</dd>
        </div>
        <div>
          <dt>Почта</dt>
          <dd data-testid="drawer-email">{emailLabel(teacher.email)}</dd>
        </div>
        <div className="teacher-drawer__grid--wide">
          <dt>Площадка</dt>
          <dd>{venueLabelOf(teacher, options)}</dd>
        </div>
      </dl>
      {sections.map((section) => (
        <div className="teacher-drawer__group" key={section.key} data-testid={`drawer-group-${section.key}`}>
          <h4 className="teacher-drawer__title">{section.title}</h4>
          <dl className="teacher-drawer__grid">
            {section.fields.map((field) => (
              <div className={field.multiline ? 'teacher-drawer__grid--wide' : undefined} key={field.key}>
                <dt>{field.label}</dt>
                <dd data-testid={`drawer-${field.key}`}>{attributeLabel(teacher[field.key] ?? null)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      <p className="teacher-drawer__hint" data-testid="drawer-locked">
        Редактирование доступно ролям не ниже менеджера.
      </p>
    </>
  );
}

/**
 * The write half (MANAGER+, and since v1.9.11/T.Z71 a КУРАТОР of its own venues):
 * contacts + the 19 attribute fields, one form, one PATCH. The venue picker offers the
 * session's scope only (`options` is already scope-locked in the page), and a FAILED
 * dictionary read renders the retry alert instead of an empty combobox — the same rule
 * the toolbar's «Площадка» follows. The delete is NOT a field of the form — its footer
 * button only OPENS the confirmation drawer (`onDelete`), because it is the one write
 * here that cannot be undone by re-typing; the request itself lives there.
 */
function TeacherEditor({
  teacher,
  options,
  venueState,
  onRetryVenue,
  onClose,
  onDelete,
}: {
  teacher: TeacherDto;
  options: ReturnType<typeof venueComboboxOptions>;
  venueState: VenueDirectoryState;
  onRetryVenue: () => void;
  onClose: () => void;
  onDelete: (teacher: TeacherDto) => void;
}) {
  const { role } = useAuth();
  const [draft, setDraft] = useState<TeacherDraft>(() => draftOf(teacher));
  const [statusDraft, setStatusDraft] = useState<TeacherStatus>(teacher.status);
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const patch = usePatchTeacher();
  const pending = patch.isPending;

  const errors: DraftErrors = touched ? validateDraft(draft) : {};
  // ADDITIVE (v1.9.17, T.Z89) — the status editor exists only where the api admits it:
  // an ADMIN and a МЕНЕДЖЕР may set any state, a КУРАТОР may not (their «на удаление»
  // goes through the «Удалить» button, which marks for them).
  const statusOptions = statusOptionsFor(role);
  // ADDITIVE (v1.9.17, T.Z91) — a КУРАТОР edits the reduced block only; the api omits
  // the ten cells and the form never renders them for that session.
  const fields = attributeFieldsFor(role);
  const sections = attributeSectionsFor(role);

  const setContact = (key: 'fullName' | 'phone' | 'email') => (value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const setAttribute = (key: keyof typeof draft.attributes) => (value: string) =>
    setDraft((current) => ({ ...current, attributes: { ...current.attributes, [key]: value } }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    const nextErrors = validateDraft(draft);
    if (draftHasErrors(nextErrors)) return;
    setServerError(null);
    const body = { ...toPatchBody(teacher, draft, fields) };
    // The status rides the same PATCH when it moved — a no-op body plus a no-op status
    // stays the api's documented no-op (200, nothing written, no audit row).
    if (statusOptions.length > 0 && statusDraft !== teacher.status) body['status'] = statusDraft;
    // A patch that changes nothing is the api's documented no-op — closing the panel is
    // the whole effect, there is nothing to wait for.
    if (Object.keys(body).length > 0) {
      try {
        await patch.mutateAsync({ id: teacher.id, patch: body });
      } catch (error) {
        setServerError(describeFailure(error as ApiError | null, 'Не удалось сохранить педагога.'));
        return;
      }
    }
    onClose();
  };

  return (
    <form className="teacher-form" data-testid="teacher-form" onSubmit={submit} noValidate>
      <dl className="teacher-drawer__grid">
        <div className="teacher-drawer__grid--wide">
          <dt>Площадка</dt>
          <dd>
            {venueState.kind === 'error' ? (
              <div className="teachers-venue-error" role="alert" data-testid="teacher-venue-error">
                <span className="teachers-venue-error__text">{TEACHER_VENUE_DIRECTORY_ERROR}</span>
                <button type="button" className="btn btn--secondary btn--sm" data-testid="teacher-venue-retry" onClick={onRetryVenue}>
                  Повторить
                </button>
              </div>
            ) : (
              <Combobox
                id="teacher-venue"
                testId="teacher-venue"
                value={draft.venueId}
                options={options}
                placeholder="выберите площадку"
                ariaLabel="Площадка педагога"
                onChange={(value) => setDraft((current) => ({ ...current, venueId: value }))}
              />
            )}
            {errors.venueId ? <span className="field__error">{errors.venueId}</span> : null}
          </dd>
        </div>
        <div className="teacher-drawer__grid--wide">
          <dt>ФИО</dt>
          <dd>
            <Input
              id="teacher-full-name"
              data-testid="teacher-full-name"
              value={draft.fullName}
              onChange={(event) => setContact('fullName')(event.target.value)}
            />
          </dd>
        </div>
        <div>
          <dt>Телефон</dt>
          <dd>
            <Input
              id="teacher-phone"
              data-testid="teacher-phone"
              value={draft.phone}
              placeholder="+7 (900) 111-22-33"
              onChange={(event) => setContact('phone')(event.target.value)}
            />
          </dd>
        </div>
        <div>
          <dt>Почта</dt>
          <dd>
            <Input
              id="teacher-email"
              data-testid="teacher-email"
              type="email"
              value={draft.email}
              placeholder="ivanov@example.org"
              onChange={(event) => setContact('email')(event.target.value)}
            />
          </dd>
        </div>
        {/* ADDITIVE (v1.9.17, T.Z89) — the status, editable where the role allows it.
            Select (not chips): three mutually exclusive states, the native control the
            drawer's other enum-shaped fields would take. */}
        {statusOptions.length > 0 ? (
          <div className="teacher-drawer__grid--wide">
            <dt>Статус</dt>
            <dd>
              <select
                id="teacher-status"
                data-testid="teacher-status"
                className="input"
                value={statusDraft}
                onChange={(event) => setStatusDraft(event.target.value as TeacherStatus)}
              >
                {statusOptions.map((value) => (
                  <option key={value} value={value}>
                    {statusLabel(value)}
                  </option>
                ))}
              </select>
            </dd>
          </div>
        ) : null}
      </dl>

      {sections.map((section) => (
        <div className="teacher-drawer__group" key={section.key} data-testid={`drawer-group-${section.key}`}>
          <h4 className="teacher-drawer__title">{section.title}</h4>
          <dl className="teacher-drawer__grid">
            {section.fields.map((field) => (
              <div className={field.multiline ? 'teacher-drawer__grid--wide' : undefined} key={field.key}>
                <dt>{field.label}</dt>
                <dd>
                  {field.multiline ? (
                    <Textarea
                      data-testid={`drawer-field-${field.key}`}
                      aria-label={field.label}
                      rows={3}
                      value={draft.attributes[field.key]}
                      onChange={(event) => setAttribute(field.key)(event.target.value)}
                    />
                  ) : (
                    <Input
                      data-testid={`drawer-field-${field.key}`}
                      aria-label={field.label}
                      value={draft.attributes[field.key]}
                      onChange={(event) => setAttribute(field.key)(event.target.value)}
                    />
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}

      {serverError ? (
        <p className="teacher-form__error" role="alert" data-testid="teacher-form-error">
          {serverError}
        </p>
      ) : null}

      <footer className="teacher-form__footer">
        {/* ADDITIVE (v1.9.12, T.Z80) — the delete is visible to every role that can
            write (`canDeleteTeachers` === `canWriteTeachers`, the api's own DELETE
            grant); the button only ASKS — the request is produced in the confirmation
            drawer this opens. ADDITIVE (v1.9.17, T.Z89) — the caption says what the
            click will DO for this role: an ADMIN removes, everyone else marks
            «на удаление». */}
        <Button variant="danger" size="sm" data-testid="teacher-delete" onClick={() => onDelete(teacher)}>
          {role === 'ADMIN' ? 'Удалить' : 'Удалить (на удаление)'}
        </Button>
        <span className="teacher-form__spacer" />
        <Button variant="secondary" size="sm" onClick={onClose}>
          Отмена
        </Button>
        <Button variant="primary" size="sm" type="submit" loading={pending} data-testid="teacher-save">
          Сохранить
        </Button>
      </footer>
    </form>
  );
}

/**
 * ADDITIVE (v1.9.12, T.Z80) — «Удалить педагога?» — the confirmation drawer of the
 * delete, the cabinet's #26.4 shape (`PrefillDeleteDrawer`): it names the row by ФИО
 * and venue, says what the delete does to the учебные группы, and holds the ONLY code
 * path that reaches `DELETE /teachers/:id` — through `deleteRequestId`, which answers
 * an id for the confirmed layer and `null` for anything else. A delete that worked
 * closes both layers via `onDeleted` (the T.Z24 lesson); a refused one keeps the
 * drawer open with the api's message.
 */
function TeacherDeleteConfirm({
  layer,
  onClose,
  onDeleted,
}: {
  layer: Extract<TeacherDeleteLayer, { kind: 'confirm' }>;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { role } = useAuth();
  const remove = useDeleteTeacher();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // ADDITIVE (v1.9.17, T.Z89) — the copy says WHICH delete this session is asking for:
  // an ADMIN removes the row, a МЕНЕДЖЕР/КУРАТОР marks it «на удаление» for the ADMIN
  // to rule on.
  const copy = teacherDeleteCopy(layer.teacher, role);
  const requestId = deleteRequestId(layer);

  const confirm = async () => {
    if (requestId === null || remove.isPending) return;
    setError(null);
    try {
      await remove.mutateAsync(requestId);
      // The drawers unmount on close, which is exactly why the roster is refreshed
      // HERE: an unmounted mutation's own onSuccess never fires.
      await invalidateTeachers(queryClient);
      onDeleted();
    } catch (requestError) {
      setError(describeFailure(requestError as ApiError | null, 'Не удалось удалить педагога.'));
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={copy.title}
      description={copy.description}
      footer={
        <>
          <Button variant="danger" data-testid="teacher-delete-confirm" loading={remove.isPending} onClick={() => void confirm()}>
            {role === 'ADMIN' ? 'Удалить' : 'Пометить «на удаление»'}
          </Button>
          <Button variant="ghost" data-testid="teacher-delete-cancel" disabled={remove.isPending} onClick={onClose}>
            Отмена
          </Button>
        </>
      }
    >
      <div data-testid="teacher-delete-drawer">
        <p className="teacher-delete-note" data-testid="teacher-delete-row">
          {copy.body}
        </p>
        <p className="teacher-delete-note teacher-delete-note--consequence" data-testid="teacher-delete-hint">
          {copy.hint}
        </p>
        {error !== null ? (
          <p className="field__error" role="alert" data-testid="teacher-delete-error">
            {error}
          </p>
        ) : null}
      </div>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// The create dialog — T.Z77 opens it on the page's «Площадка» filter when the form can
// offer that venue; a new row still has no attributes to fill yet.
// ---------------------------------------------------------------------------

function TeacherCreator({
  initialVenueId,
  options,
  venueState,
  onRetryVenue,
  onClose,
}: {
  /** T.Z77 — the prefilled «Площадка» (`createVenuePrefill`, `''` = let the user pick). */
  initialVenueId: string;
  options: ReturnType<typeof venueComboboxOptions>;
  venueState: VenueDirectoryState;
  onRetryVenue: () => void;
  onClose: () => void;
}) {
  // The prefill is read ONCE, at mount (the modal mounts on the «Добавить педагога»
  // click and the toolbar is unreachable while it is open), so a filter change can
  // never swap the venue under a half-filled form.
  const [draft, setDraft] = useState<TeacherDraft>(() => ({
    ...EMPTY_DRAFT,
    venueId: initialVenueId,
    attributes: { ...EMPTY_DRAFT.attributes },
  }));
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useCreateTeacher();
  const errors: DraftErrors = touched ? validateDraft(draft) : {};

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    const nextErrors = validateDraft(draft);
    if (draftHasErrors(nextErrors)) return;
    setServerError(null);
    try {
      await create.mutateAsync(toCreateBody(draft));
      onClose();
    } catch (error) {
      setServerError(describeFailure(error as ApiError | null, 'Не удалось сохранить педагога.'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Новый педагог"
      description="ФИО и площадка обязательны; телефон и почта — по желанию."
    >
      <form className="teacher-form" data-testid="teacher-form" onSubmit={submit} noValidate>
        <Field label="Площадка" htmlFor="teacher-create-venue" required error={errors.venueId}>
          {venueState.kind === 'error' ? (
            <div className="teachers-venue-error" role="alert" data-testid="teacher-venue-error">
              <span className="teachers-venue-error__text">{TEACHER_VENUE_DIRECTORY_ERROR}</span>
              <button type="button" className="btn btn--secondary btn--sm" data-testid="teacher-venue-retry" onClick={onRetryVenue}>
                Повторить
              </button>
            </div>
          ) : (
            <Combobox
              id="teacher-create-venue"
              testId="teacher-venue"
              value={draft.venueId}
              options={options}
              placeholder="выберите площадку"
              ariaLabel="Площадка педагога"
              onChange={(value) => setDraft((current) => ({ ...current, venueId: value }))}
            />
          )}
        </Field>
        <Field label="ФИО" htmlFor="teacher-create-full-name" required error={errors.fullName}>
          <Input
            id="teacher-create-full-name"
            data-testid="teacher-full-name"
            value={draft.fullName}
            placeholder="Иванов Иван Иванович"
            onChange={(event) => setDraft((current) => ({ ...current, fullName: event.target.value }))}
          />
        </Field>
        <Field label="Телефон" htmlFor="teacher-create-phone" error={errors.phone} hint="Как в реестре: +7 (900) 111-22-33">
          <Input
            id="teacher-create-phone"
            data-testid="teacher-phone"
            value={draft.phone}
            placeholder="+7 (900) 111-22-33"
            onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))}
          />
        </Field>
        <Field label="Почта" htmlFor="teacher-create-email" error={errors.email} hint="Сохраняется строчными буквами">
          <Input
            id="teacher-create-email"
            data-testid="teacher-email"
            type="email"
            value={draft.email}
            placeholder="ivanov@example.org"
            onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
          />
        </Field>
        {serverError ? (
          <p className="teacher-form__error" role="alert" data-testid="teacher-form-error">
            {serverError}
          </p>
        ) : null}
        <footer className="teacher-form__footer">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" size="sm" type="submit" loading={create.isPending} data-testid="teacher-save">
            Добавить
          </Button>
        </footer>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/** Same shape the table stands in for, so nothing jumps when the data lands. */
function RosterSkeleton() {
  return (
    <div data-testid="teachers-skeleton" className="teacher-skeleton">
      {Array.from({ length: 6 }, (_, index) => (
        <span className="teacher-skeleton__line" key={index} />
      ))}
    </div>
  );
}

/** The api's message when it has one, a readable fallback when it does not. */
function describeFailure(error: ApiError | null, fallback: string): string {
  return error?.message ?? fallback;
}
